import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { match, P } from 'ts-pattern';
import Card from '~/components/Card';
import LinkButton from '~/components/LinkButton';
import Page from '~/components/Page';
import { StoreNotFoundError } from '~/utils/errors';
import { logError } from '~/utils/observability';
import { useRouteError } from '~/utils/routing';

function ErrorLayout({ children }: { children: React.ReactNode }) {
  return (
    <main className="width-full min-h-full bg-gray-100 px-6 py-24 sm:py-32 lg:px-8">
      <div className="mx-auto max-w-2xl">{children}</div>
    </main>
  );
}

function DefaultError({ error }: { error: Error }) {
  const params = useParams();

  useEffect(() => {
    if (error) {
      logError(error);
    }
  }, [error]);

  return (
    <ErrorLayout>
      <Page headline="Something went wrong...">
        <Card>
          <p>Please go home and try again.</p>
          <LinkButton
            className="mt-6 w-full sm:w-1/2"
            to={`/${params.store ?? ''}`}
            reloadDocument // force a reload to hopefully get any app updates
          >
            Home
          </LinkButton>
        </Card>
      </Page>
    </ErrorLayout>
  );
}

function StoreNotFound({ error }: { error: StoreNotFoundError }) {
  useEffect(() => {
    if (error) {
      logError(error);
    }
  }, [error]);

  return (
    <ErrorLayout>
      <Page headline="Store Not Found">
        <Card>
          <p>
            We were unable to find a store for &ldquo;{error.slug}&rdquo;.
            Please check to make sure that is correct.
          </p>
        </Card>
      </Page>
    </ErrorLayout>
  );
}

export default function ErrorPage() {
  const error = useRouteError();

  // Remove the splash screen
  useEffect(() => {
    const body = document.getElementById('crew');

    if (!body) {
      throw new Error('Body not found');
    }
    body.classList.add('ready');
  }, []);

  return match(error)
    .with(P.instanceOf(StoreNotFoundError), (e) => <StoreNotFound error={e} />)
    .otherwise((e) => <DefaultError error={e} />);
}
